/* CC interactions. Sitewide behaviour layer. */
(() => {
  const root = document.documentElement;

  // Measure the announcement bar so the hero can fit exactly within the
  // viewport (100vh minus header minus announcement bar). Dawn's header
  // custom element already sets --header-height; the announcement bar
  // sits outside that element so it needs its own measurement.
  const announce = document.querySelector('.cc-announce');
  const setAnnounceHeight = () => {
    root.style.setProperty('--cc-announce-height', `${announce ? announce.offsetHeight : 0}px`);
  };
  setAnnounceHeight();
  window.addEventListener('resize', setAnnounceHeight);

  // Sticky header quiet-on-scroll. Toggles .cc-scrolled on <html> past a threshold.
  let scrolled = false;
  const onScroll = () => {
    const y = window.scrollY || document.documentElement.scrollTop;
    const nowScrolled = y > 24;
    if (nowScrolled !== scrolled) {
      scrolled = nowScrolled;
      root.classList.toggle('cc-scrolled', scrolled);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Fade-in on scroll. Applies .is-visible to .cc-fade-in elements as
  // they enter the viewport. Elements that are already in view at load
  // (above-the-fold) get revealed synchronously in the same tick so no
  // section flashes invisibly on load — the previous safety-net delay
  // (2s) was long enough on mobile Safari that visitors saw a blank
  // section before the reveal fired.
  const fadeSelector = '.cc-fade-in';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealAllNow = () => {
    document.querySelectorAll(fadeSelector).forEach((el) => el.classList.add('is-visible'));
  };
  const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };
  if (reduced) {
    revealAllNow();
  } else if ('IntersectionObserver' in window) {
    // Reveal above-the-fold synchronously — do not wait for IO to fire.
    document.querySelectorAll(fadeSelector).forEach((el) => {
      if (isInViewport(el)) el.classList.add('is-visible');
    });

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      }
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });

    const observeAll = () => {
      document.querySelectorAll(fadeSelector).forEach((el) => {
        if (!el.classList.contains('is-visible')) io.observe(el);
      });
    };
    observeAll();
    // Some sections stream in later (Dawn quick-view etc). Watch mutations.
    new MutationObserver(observeAll).observe(document.body, { childList: true, subtree: true });
    // No safety-net timer. A short one force-revealed every below-fold
    // section before the visitor could scroll to it, killing scroll
    // motion sitewide. If IntersectionObserver truly fails to fire on a
    // supported browser, that is a bigger regression than a fade — let
    // it surface. Above-the-fold reveal is handled synchronously above.
  } else {
    revealAllNow();
  }

  // 2026-10-02: tells the 3s fallback in theme.liquid that the reveal is in
  // hand, so it leaves .cc-js alone. Set HERE, after every branch above has
  // run, and not at the end of the file: an error in a later block should
  // cause a harmless full reveal, never a page that stays blank because a
  // flag was set before the observer that justifies it existed.
  window.ccFadeReady = true;

  // Sitewide scroll-linked Ken Burns for photo containers. Where
  // animation-timeline: view() is supported (Safari 18+, Chromium 115+)
  // the CSS in cc-layout.css does the work. On iOS Safari <18 the CSS
  // no-ops, so the JS fallback below picks it up via IntersectionObserver
  // and animates each photo container from scale(1.05) to scale(1) once
  // as it enters view — a one-shot settle rather than a scroll-linked
  // scrub, but visually reads as "photos come alive as you scroll".
  const supportsViewTimeline = window.CSS && CSS.supports && CSS.supports('animation-timeline: view()');
  if (!reduced && !supportsViewTimeline && 'IntersectionObserver' in window) {
    const kenBurnsSel = '.cc-card__frame, .cc-studio-work__photo, .cc-lens__portrait, .cc-about__portrait, .cc-pdp__hero, .cc-portfolio__frame, .cc-paper__figure';
    const kenIo = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('cc-ken-burns-in');
          kenIo.unobserve(entry.target);
        }
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    const observeKen = () => {
      document.querySelectorAll(kenBurnsSel).forEach((el) => {
        if (!el.classList.contains('cc-ken-burns-in')) kenIo.observe(el);
      });
    };
    observeKen();
    new MutationObserver(observeKen).observe(document.body, { childList: true, subtree: true });
  }

  // Scroll progress bar. CSS drives this via animation-timeline: scroll(root)
   // where supported (Chromium 115+, Safari 18+). For older browsers,
   // this JS fills the same bar off passive scroll listener so the cue
   // still works — sleek "keep going" signal that holds reading attention.
  const progressFill = document.querySelector('.cc-scroll-progress-fill');
  const hasScrollTimeline = window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()');
  if (progressFill && !hasScrollTimeline && !reduced) {
    let ticking = false;
    const updateProgress = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
      progressFill.style.transform = `scaleX(${ratio})`;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(updateProgress); ticking = true; }
    }, { passive: true });
    updateProgress();
  }

  // Hero parallax. Now targets the .cc-hero__media parent element so
  // it composes cleanly with the child image's load-time scale
  // animation — no compound transforms, no CSS-variable indirection
  // inside a keyframe. Skipped under reduced motion.
  const heroMedia = document.querySelector('.cc-hero__media');
  const heroSection = heroMedia ? heroMedia.closest('.cc-hero') : null;
  if (heroMedia && heroSection && !reduced) {
    let ticking = false;
    const updateParallax = () => {
      const rect = heroSection.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        ticking = false;
        return;
      }
      const offset = Math.max(0, -rect.top) * 0.1;
      heroMedia.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
    updateParallax();
  }

  // Motion system: cart pulse after ATC. cc-pdp.js writes a timestamp
  // to localStorage on Add-to-Cart submit; the cart-icon-bearing page
  // that loads next picks up the flag and pulses the header cart icon
  // once (700ms) then clears the flag. Skipped under reduced motion.
  if (!reduced) {
    try {
      const stamp = localStorage.getItem('cc:cart-just-added');
      if (stamp && Date.now() - Number(stamp) < 8000) {
        const cartIcon = document.querySelector(
          '.header__icon--cart, .cart-count-bubble, [href="/cart"] .icon, [data-cart-count]'
        );
        if (cartIcon) {
          cartIcon.classList.add('cc-cart-pulse');
          setTimeout(() => cartIcon.classList.remove('cc-cart-pulse'), 800);
        }
        localStorage.removeItem('cc:cart-just-added');
      }
    } catch (_) { /* localStorage unavailable — no pulse, no crash */ }
  }

  // First-order discount popup. One-time-dismissible via localStorage.
  const POPUP_KEY = 'cc:discount-popup:v1';
  const popup = document.querySelector('[data-cc-discount-popup]');
  if (popup) {
    const shouldShow = () => {
      try { return !localStorage.getItem(POPUP_KEY); } catch (_) { return true; }
    };
    const dismiss = () => {
      popup.classList.remove('is-open');
      try { localStorage.setItem(POPUP_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    };
    const open = () => { popup.classList.add('is-open'); };

    if (shouldShow()) {
      // Small delay so it doesn't punch the hero on load.
      setTimeout(open, 6000);
    }
    popup.querySelectorAll('[data-cc-popup-close]').forEach((btn) => {
      btn.addEventListener('click', dismiss);
    });
    popup.addEventListener('click', (event) => {
      if (event.target === popup) dismiss();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) dismiss();
    });
    const form = popup.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        // Shopify handles the actual signup redirect; hide next visits.
        try { localStorage.setItem(POPUP_KEY, String(Date.now())); } catch (_) {}
      });
    }
  }

  // 2026-08-24: sitewide tagline bar (.cc-tagline-bar, sections/header.liquid)
  // scroll-hide, per direct instruction to match the homepage's own
  // .cc-float-tagline hide-on-scroll-down/reveal-on-scroll-up behaviour
  // (sections/cc-home-v3.liquid) exactly - same 64px threshold, same
  // is-hidden class, same passive+rAF-throttled scroll listener pattern
  // already used elsewhere in this file. CSS half (transform/transition,
  // .is-hidden rule) is in assets/cc-nav-clarity.css. querySelector (not
  // getElementById) since the element carries no id; .cc-tagline-bar is
  // always present in the DOM sitewide (hidden via display:none on the
  // homepage and while a header dropdown is open, per that CSS file's
  // own :has() rules) - this scroll-hide layers on top of that state
  // harmlessly since is-hidden and display:none are independent.
  const taglineBar = document.querySelector('.cc-tagline-bar');
  if (taglineBar) {
    // 2026-08-24 (later same day): --cc-tagline-bar-height, read by
    // .cc-tagline-bar.is-hidden in assets/cc-nav-clarity.css. A live
    // browser test found this element's percentage transform
    // (translateY(-100%)) was not resolving against its own rendered
    // height, so is-hidden barely moved it - measuring the real height
    // in JS and driving the hide transform off that pixel value instead
    // sidesteps the problem entirely (same approach this theme already
    // uses for --header-height in sections/header.liquid's StickyHeader).
    // Re-measured on load and on the same 990px breakpoint crossing
    // StickyHeader itself re-measures at, since this bar is two lines
    // (taller) on mobile and one line (shorter) on desktop.
    const setTaglineBarHeight = () => {
      document.documentElement.style.setProperty('--cc-tagline-bar-height', `${taglineBar.offsetHeight}px`);
    };
    setTaglineBarHeight();
    window.matchMedia('(max-width: 990px)').addEventListener('change', setTaglineBarHeight);

    const TAGLINE_HIDE_THRESHOLD = 64;
    let taglineLastY = window.scrollY || window.pageYOffset || 0;
    let taglineTicking = false;
    const onTaglineScroll = () => {
      const currentY = window.scrollY || window.pageYOffset || 0;
      if (currentY > taglineLastY && currentY > TAGLINE_HIDE_THRESHOLD) {
        taglineBar.classList.add('is-hidden');
      } else if (currentY < taglineLastY) {
        taglineBar.classList.remove('is-hidden');
      }
      taglineLastY = currentY;
      taglineTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!taglineTicking) {
        window.requestAnimationFrame(onTaglineScroll);
        taglineTicking = true;
      }
    }, { passive: true });
  }
})();

/* ---------------------------------------------------------------------------
   Lazy inline video, added 2026-09-12.

   sections/cc-studio-portfolio.liquid used to carry autoplay + preload="auto"
   on its inline showreel. Measured on the live Studio page, that pulled the
   whole 15.16 MB of cc-oakberry-apres-ski.mp4 on load - readyState 4 - for a
   video sitting well below the fold. Every visit on mobile data paid for it
   whether the visitor scrolled that far or not.

   autoplay had to go rather than just be softened, because autoplay forces a
   load and would have defeated preload="none" on its own. So the source now
   sits in data-src and is attached here, once, when the element is close to
   the viewport. Behaviour for anyone who reaches the section is unchanged:
   muted, looping, inline, playing by the time it is on screen.

   rootMargin gives it a screen of warning so the first frame is ready rather
   than starting black. The observer disconnects per element after attaching -
   this must never run twice, or the source is appended twice and the video
   restarts. Browsers without IntersectionObserver simply get the source
   immediately, which is the old behaviour and still correct.
--------------------------------------------------------------------------- */
(function(){
  var vids = document.querySelectorAll('video[data-cc-lazy-video][data-src]');
  if (!vids.length) return;

  function attach(v){
    if (v.dataset.ccVideoAttached) return;
    v.dataset.ccVideoAttached = '1';
    var s = document.createElement('source');
    s.type = 'video/mp4';
    s.src = v.dataset.src;
    v.appendChild(s);
    v.preload = 'auto';
    v.load();
    /* play() rejects on some browsers until the user has interacted. The
       catch keeps that from throwing into the console - the poster stays up
       and the controls-less loop simply waits, which is the graceful case. */
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function(){});
  }

  if (!('IntersectionObserver' in window)){
    Array.prototype.forEach.call(vids, attach);
    return;
  }

  var io = new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if (!e.isIntersecting) return;
      attach(e.target);
      io.unobserve(e.target);
    });
  }, { rootMargin: '100% 0px' });

  Array.prototype.forEach.call(vids, function(v){ io.observe(v); });
})();

/* ---------------------------------------------------------------------------
   2026-10-13 — the collection card's room mock-up loads on hover, not on sight

   .cc-card__mock is a hover-reveal layer: opacity 0 until :hover or
   :focus-visible (cc-sections.css:549-557). It was nonetheless fetched on
   every card at a flat 1200w - 235 KB each as WebP, measured, up to 26 per
   collection page - including on phones, where there is no hover and the
   layer can never be seen at all.

   The URL now sits in data-src/data-srcset and is attached on first
   pointerenter, and only for a pointer that can hover. A coarse pointer
   never attaches anything, so a phone pays nothing for a layer it cannot
   see. Keyboard users get it too, via focusin, because the CSS reveals the
   layer on :focus-visible as well.

   Attaches once per card and then stops listening. If this never runs the
   overlay simply never appears - acceptable for a decorative, aria-hidden
   layer, and strictly better than what it replaced.
--------------------------------------------------------------------------- */
(function(){
  var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var imgs = document.querySelectorAll('img[data-cc-mock][data-src]');
  if (!imgs.length) return;

  function attach(img){
    if (img.dataset.ccMockAttached) return;
    img.dataset.ccMockAttached = '1';
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
  }

  /* Attaching on pointerenter would be the obvious thing and is wrong: the
     CSS reveals the layer immediately on :hover, so the visitor would get an
     empty box for as long as the fetch takes. A hover-reveal has to already
     be loaded by the time it is revealed.

     So on a hover-capable pointer this behaves exactly as before - attached
     when the card nears the viewport - and the whole saving comes from a
     coarse pointer attaching nothing at all. Desktop is unchanged; the phone
     stops paying for a layer it can never see. */
  function observe(img){
    var card = img.closest('.cc-card') || img;
    if (!('IntersectionObserver' in window)) { attach(img); return; }
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){ if (e.isIntersecting) { attach(img); io.disconnect(); } });
    }, { rootMargin: '400px 0px' });
    io.observe(card);
  }

  imgs.forEach(function(img){
    var card = img.closest('.cc-card');
    /* focusin whatever the pointer: a keyboard user on a touch laptop still
       reveals the layer via :focus-visible and should not meet an empty box. */
    if (card) {
      var onFocus = function(){ attach(img); card.removeEventListener('focusin', onFocus); };
      card.addEventListener('focusin', onFocus, { passive: true });
    }
    if (canHover) observe(img);
  });
})();
